import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getSpacesRedirectLocation } from '../components/AppLayout/utils/spaceSectionUrl';
import { useAppLayoutConfig } from '../providers/AppLayoutConfig/AppLayoutConfigContext';
import type { LayoutSection } from '../types/LayoutConfig';
import { useCanonicalLayoutIdForSection } from './useAppLayoutId';
import { useSpacesRouting } from './useSpacesRouting';

/**
 * When the URL should be rewritten for spaces routing, returns the full location string; otherwise `null`.
 * @param routeNavGroupSlug — first segment after `/:section` when the grouped route matched (from route params).
 */
export function useRedirectSpacesRoute(
  section: LayoutSection,
  routeNavGroupSlug: string | undefined
): string | undefined {
  const location = useLocation();
  const { layoutId } = useParams<{ layoutId: string }>();
  const { layoutConfigsMap } = useAppLayoutConfig(section);
  const canonicalLayoutId = useCanonicalLayoutIdForSection(section);
  const { indexRedirectPath } = useSpacesRouting(section, canonicalLayoutId);

  return useMemo(
    () =>
      getSpacesRedirectLocation({
        section,
        location,
        layoutId,
        routeNavGroupSlug,
        layoutConfigsMap,
        indexRedirectPath,
      }),
    [section, location, layoutId, routeNavGroupSlug, layoutConfigsMap, indexRedirectPath]
  );
}
